import { fetchTokenAuth } from "../standardAuthPatterns";
import { resolveBaseUrl } from "../baseUrl";
import { productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";

const apiBaseUrl = (baseUrl: string | undefined) => {
  const url = resolveBaseUrl(baseUrl, "https://api.flexera.com/graphql");
  return url.includes("://") ? url : `https://${url}`;
};

export const flexeraItVisibility = productVariant({
  identifier: "flexeraItVisibility",
  authLabel: "IT Visibility",
  protocol: "http",
  defaultUrlPattern: ({ baseUrl }) => exactUrlToRegex(apiBaseUrl(baseUrl)),
  auth: ({ configuration }) =>
    fetchTokenAuth({
      request: {
        url: {
          from: "literal",
          value:
            "https://login.flexera." +
            (apiBaseUrl(configuration.baseUrl)
              .match(/^https:\/\/api\.flexera\.(com|eu|au)(?:\/|$)/i)?.[1]
              ?.toLowerCase() || "com") +
            "/oidc/token",
        },
        headers: [],
        formBody: {
          client_id: { from: "secret", field: "clientId" },
          client_secret: { from: "secret", field: "clientSecret" },
          grant_type: { from: "literal", value: "client_credentials" },
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: {
        kind: "header",
        name: "Authorization",
        scheme: "Bearer",
        extraHeaders: [],
      },
    }),
  steps: [
    {
      title: "Set up Flexera IT Visibility",
      items: [
        {
          kind: "markdown",
          content:
            "Create a dedicated service account and client using the [Flexera One service account instructions](https://docs.flexera.com/flexera-one/flexera-api/working-with-the-flexera-one-api/using-a-service-account-with-the-flexera-one-apis). Grant read access to your organization’s software inventory, then copy the client ID and secret. Enter the organization ID and regional [GraphQL API](https://developer.flexera.com/docs/api/graphql/v1) base URL below. The connection test reads at most one software name.",
        },
      ],
    },
    {
      title: "Enter connection details",
      items: [
        {
          kind: "baseUrl",
          label: "API base URL",
          required: true,
          defaultValue: "https://api.flexera.com/graphql/",
          target: {
            kind: "configuration",
            field: "baseUrl",
          },
          helperText:
            "Use the GraphQL API base matching your Flexera One zone: NAM https://api.flexera.com/graphql/, EU https://api.flexera.eu/graphql/, or APAC https://api.flexera.au/graphql/. Copy the organization ID from your Flexera One organization URL.",
        },
        {
          kind: "input",
          label: "Service account client ID",
          required: true,
          target: {
            kind: "secrets",
            field: "clientId",
          },
          helperText:
            "Client ID returned when you create a Flexera One service-account client.",
        },
        {
          kind: "input",
          label: "Service account client secret",
          required: true,
          target: {
            kind: "secrets",
            field: "clientSecret",
          },
          helperText:
            "Client secret returned with the Flexera One service-account client. Store it securely.",
        },
        {
          kind: "input",
          label: "Organization ID",
          required: true,
          target: {
            kind: "configuration",
            field: "orgId",
          },
          helperText:
            "Copy the organization ID from your Flexera One organization URL.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl, orgId }) => ({
      url:
        apiBaseUrl(baseUrl).replace(/\/+$/, "") +
        "/v1/orgs/" +
        encodeURIComponent(orgId) +
        "/graphql",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: '{"query":"query ConnectionTest { software(limit: 1) { name } }"}',
      expectedBodyContains: ['"data"', '"software"'],
    }),
  },
});
